import type { ReactNode } from 'react'

import type {
  Notification,
  NotificationClickHandler,
} from '@/components/notifications/notification.interface'
import { NotificationCenter } from '@/components/notifications/notification-center.molecule'
import { SidebarTrigger } from '@/components/ui/sidebar'

import { ComingSoonDialogs } from './coming-soon-dialogs.molecule'
import type {
  GlobalSearchResultGroup,
  GlobalSearchResultItem,
  GlobalSearchResultType,
} from './global-search.interface'
import { GlobalSearch } from './global-search.molecule'
import { HeaderNavButtons } from './header-nav-buttons.molecule'
import { LogoText } from './logo-text.atom'
import { useComingSoonDialogs } from './use-coming-soon-dialogs.hook'

interface AppLayoutHeaderProps {
  searchResultGroupList: GlobalSearchResultGroup[]
  isSearching?: boolean
  notificationList: Notification[]
  onGlobalSearch: (query: string) => void
  onResultSelect: (type: GlobalSearchResultType, item: GlobalSearchResultItem) => void
  onAgenciesClick: () => void
  onConnectsClick: () => void
  onOrgSettings: () => void
  onNotificationClick: NotificationClickHandler
  onMarkAllRead: () => void
}

export const AppLayoutHeader = ({
  searchResultGroupList,
  isSearching,
  notificationList,
  onGlobalSearch,
  onResultSelect,
  onAgenciesClick,
  onConnectsClick,
  onOrgSettings,
  onNotificationClick,
  onMarkAllRead,
}: AppLayoutHeaderProps): ReactNode => {
  const comingSoonDialogs = useComingSoonDialogs()

  return (
    <header className="layout-header flex h-14 items-center gap-4 border-b px-4 lg:h-[60px] lg:px-6">
      <SidebarTrigger className="sidebar-toggle -ml-1" />
      <div className="hidden xl:block">
        <LogoText className="max-w-[180px] mt-[-1px] ml-1" />
      </div>
      <div className="flex-1 flex justify-center w-full md:max-w-xl mx-auto">
        <GlobalSearch
          placeholder="Search projects, agencies, chats..."
          resultGroupList={searchResultGroupList}
          onSearch={onGlobalSearch}
          onResultSelect={onResultSelect}
          isLoading={isSearching}
        />
      </div>
      <div className="flex items-center gap-2">
        <HeaderNavButtons
          onAgenciesClick={onAgenciesClick}
          onOrgSettings={onOrgSettings}
          onToolsClick={comingSoonDialogs.handleToolsClick}
          onConnectsClick={onConnectsClick}
        />
        {/* TODO: Re-enable when notifications feature is ready */}
        <div className="hidden">
          <NotificationCenter
            notificationList={notificationList}
            onNotificationClick={onNotificationClick}
            onMarkAllRead={onMarkAllRead}
          />
        </div>
      </div>
      <ComingSoonDialogs toolsDialog={comingSoonDialogs.toolsDialog} />
    </header>
  )
}
